import type { EChartsOption } from 'echarts';
import type { ChartOptionBuildOptions } from './chart-option-chrome';
import {
  buildSharedChrome,
  buildTooltipStyle,
  hexToRgba,
  pickColor,
} from './chart-option-chrome';
import { resolveEchartsThemeTokens } from './resolve-echarts-theme-tokens';

/**
 * Input for {@link createRadarChartOptions}.
 *
 * @example
 * const input: RadarChartInput = {
 *   indicators: [{ name: 'CNC', max: 100 }],
 *   series: [{ name: 'Plant A', values: [80] }],
 * };
 */
export type RadarChartInput = {
  indicators: readonly { name: string; max: number }[];
  series: readonly { name: string; values: number[] }[];
  colors?: string[];
};

/**
 * Generate an ECharts radar chart for multi-axis capability comparisons.
 *
 * @param input Indicators and series polygons. {@link RadarChartInput}
 * @param buildOptions Optional theme / toolbox overrides.
 * @returns A bare {@link EChartsOption} (not a {@link SupportedChartConfiguration}).
 * @example
 * const opt = createRadarChartOptions({
 *   indicators: [{ name: 'CNC', max: 100 }],
 *   series: [{ name: 'Plant A', values: [80] }],
 * });
 */
export function createRadarChartOptions(
  input: RadarChartInput,
  buildOptions: ChartOptionBuildOptions = {}
): EChartsOption {
  const theme = buildOptions.theme ?? resolveEchartsThemeTokens();
  const palette = input.colors ?? theme.palette;

  return {
    ...buildSharedChrome(theme, buildOptions.showToolbox === true),
    tooltip: {
      ...buildTooltipStyle(theme),
      trigger: 'item',
    },
    legend: {
      show: true,
      bottom: 0,
      textStyle: { color: theme.mutedColor },
    },
    radar: {
      indicator: input.indicators.map((indicator) => ({
        name: indicator.name,
        max: indicator.max,
      })),
      axisName: { color: theme.mutedColor },
      splitLine: { lineStyle: { color: theme.splitLineColor } },
      splitArea: {
        areaStyle: {
          color: [
            hexToRgba(theme.borderColor, 0.05),
            hexToRgba(theme.borderColor, 0.12),
          ],
        },
      },
    },
    series: [
      {
        type: 'radar',
        data: input.series.map((entry, index) => ({
          name: entry.name,
          value: entry.values,
          itemStyle: { color: pickColor(palette, index) },
          areaStyle: {
            color: hexToRgba(pickColor(palette, index), 0.2),
          },
        })),
      },
    ],
  };
}
